import { AddAgencyClient } from '@src/clientUser/agency/AddAgencyClient';
import { ClientUsersSideNav } from '@src/clientUser/ClientUsersSideNav';
import { CompaniesPage } from '@src/clientUser/company/CompaniesPage';
import { CompanyPage } from '@src/clientUser/company/CompanyPage';
import { ClientUserEditOrder } from '@src/clientUser/editOrder/ClientUserEditOrder';
import { Layout } from '@src/common/components/Layout';
import { ToastProvider } from '@src/common/context/ToastProvider';
import { PermitMeasurementContextProvider } from '@src/common/measurements/PermitMeasurementContextProvider';
import { AddOrderPermits } from '@src/orders/AddOrderPermits';
import { CompletePermitFormProvider } from '@src/orders/CompletePermitFormProvider';
import { CreateOrder } from '@src/orders/CreateOrder';
import { DuplicateOrderLoader } from '@src/orders/DuplicateOrderLoader';
import { EditOrderProvider } from '@src/orders/EditOrderProvider';
import { OrdersDashboard } from '@src/orders/OrdersDashboard';
import { OrderValuesContextProvider } from '@src/orders/OrderValuesContextProvider';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { ViewOrderProvider } from '@src/orders/ViewOrderProvider';
import { CompletePermit } from '@src/permits/completePermit/CompletePermit';
import { PermitSearch } from '@src/permits/permitSearch/PermitSearch';
import { PermitApplicationClient } from '@src/permits/services/PermitApplicationClient';
import { PublicPortal } from '@src/publicPortal/PublicPortal';
import { AwaitRouterData } from '@src/routing/AwaitRouterData';
import { clientTractorsAndTrailersRoutes } from '@src/routing/ClientTractorsAndTrailersRoutes';
import { GlobalErrorBoundary } from '@src/routing/components/GlobalErrorBoundary';
import { RequireAuth } from '@src/routing/components/RequireAuth';
import { UnknownRoute } from '@src/routing/components/UnknownRoute';
import { Loaders } from '@src/routing/Loaders';
import { Navigation } from '@src/routing/Navigation';
import { Refreshable } from '@src/routing/Refreshable';
import { createBrowserRouter, defer, Navigate } from 'react-router-dom';

export const clientUsersRouter = createBrowserRouter([
  {
    path: '/login',
    element: <Navigate to="/" />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Layout SideNav={ClientUsersSideNav} />
      </RequireAuth>
    ),
    errorElement: <GlobalErrorBoundary />,
    children: [
      {
        path: '',
        element: <OrdersDashboard />,
      },
      {
        path: Navigation.clients.PATH,
        children: [
          {
            path: '',
            element: <CompaniesPage />,
          },
          {
            path: Navigation.clients.get.RELATIVE,
            loader: Loaders.client.loader,
            element: (
              <AwaitRouterData path={Loaders.client.path}>
                {(data) => <CompanyPage client={data} key={data.id} />}
              </AwaitRouterData>
            ),
          },
          {
            path: Navigation.clients.addAgencyClient.RELATIVE,
            element: <AddAgencyClient />,
          },
          ...clientTractorsAndTrailersRoutes,
        ],
      },
      {
        path: Navigation.orders.PATH,
        children: [
          {
            path: Navigation.orders.create.RELATIVE,
            element: <CreateOrder />,
          },
          {
            path: Navigation.orders.order.PATH,
            loader: async ({ params }) => {
              if (!params.orderId) return;
              return { orderId: params.orderId };
            },
            element: (
              <AwaitRouterData path="orderId">
                {(orderId) => <OrderValuesContextProvider orderId={orderId} />}
              </AwaitRouterData>
            ),
            children: [
              {
                path: Navigation.orders.edit.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => (
                      <EditOrderProvider
                        orderId={orderId}
                        EditOrderPage={ClientUserEditOrder}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.completePermitForm.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId || !params.permitApplicationId) return;
                  return {
                    params: {
                      orderId: params.orderId,
                      permitApplicationId: params.permitApplicationId,
                    },
                  };
                },
                element: (
                  <AwaitRouterData path="params">
                    {({ orderId, permitApplicationId }) => (
                      <CompletePermitFormProvider
                        orderId={orderId}
                        permitId={permitApplicationId}
                        EditOrderPage={ClientUserEditOrder}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.view.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => <ViewOrderProvider orderId={orderId} />}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.duplicate.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => <DuplicateOrderLoader orderId={orderId} />}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.addPermits.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  const order = await OrdersClient.find(params.orderId);
                  return { order };
                },
                element: (
                  <AwaitRouterData path="order">
                    {(order) => <AddOrderPermits order={order} />}
                  </AwaitRouterData>
                ),
              },
            ],
          },
          {
            path: Navigation.orders.completePermit.RELATIVE,
            loader: async ({ params }) => {
              if (!params.permitId || !params.orderId) return;

              const order = OrdersClient.find(params.orderId);
              const permit = PermitApplicationClient.findPermitApplication(
                params.permitId,
              );
              return defer({
                values: Promise.all([permit, order]),
              });
            },
            element: (
              <AwaitRouterData path="values">
                {([permit, order]) => (
                  <Refreshable
                    children={() => (
                      <PermitMeasurementContextProvider
                        permitTypeCurrency={permit.permitTypeCurrency}
                      >
                        <CompletePermit
                          permitApplication={permit}
                          order={order}
                        />
                      </PermitMeasurementContextProvider>
                    )}
                  />
                )}
              </AwaitRouterData>
            ),
          },
        ],
      },
      {
        path: Navigation.permitSearch,
        element: <PermitSearch />,
      },
    ],
  },
  {
    path: Navigation.publicPortal,
    element: (
      <ToastProvider>
        <PublicPortal />
      </ToastProvider>
    ),
    errorElement: <GlobalErrorBoundary />,
  },
  {
    path: '*',
    element: <UnknownRoute />,
  },
]);
